/**
 * Token Manager
 *
 * Centralized token management for API authentication.
 * Supports both Auth0 tokens (via callback) and legacy internal JWT tokens.
 */

// Legacy internal JWT token (for backward compatibility)
let authToken: string | null = null;

// Auth0 token getter - set by Auth0Provider context
type Auth0TokenGetter = () => Promise<string>;
let auth0TokenGetter: Auth0TokenGetter | null = null;

/**
 * Set the Auth0 token getter function.
 * This is called from the App level when Auth0 is initialized.
 */
export const setAuth0TokenGetter = (getter: Auth0TokenGetter | null): void => {
  auth0TokenGetter = getter;
};

/**
 * Get the Auth0 token getter (for components that need direct access).
 */
export const getAuth0TokenGetter = (): Auth0TokenGetter | null => {
  return auth0TokenGetter;
};

/**
 * Get authentication token (async version).
 * Prioritizes Auth0 token, falls back to legacy internal JWT.
 */
export const getAuthTokenAsync = async (): Promise<string | null> => {
  // If Auth0 getter is available, use it (preferred)
  if (auth0TokenGetter) {
    try {
      return await auth0TokenGetter();
    } catch (error) {
      console.error('Failed to get Auth0 token:', error);
      // Fall through to legacy token
    }
  }

  // Fall back to legacy internal JWT token
  return authToken;
};

/**
 * Get authentication token (sync version).
 * Returns a promise if Auth0 getter is available, otherwise returns legacy token.
 * This maintains backward compatibility while supporting async Auth0 tokens.
 */
export const getAuthToken = (): string | null | Promise<string | null> => {
  // If Auth0 getter is available, return the async getter
  if (auth0TokenGetter) {
    return getAuthTokenAsync();
  }

  // Fall back to legacy internal JWT token (sync)
  return authToken;
};

/**
 * Set the legacy internal JWT token.
 * Used for email/password login flow.
 */
export const setAuthToken = (token: string | null): void => {
  authToken = token;
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

/**
 * Clear all authentication tokens.
 * Used during logout.
 */
export const clearAuthTokens = (): void => {
  authToken = null;
  auth0TokenGetter = null;
  localStorage.removeItem('auth_token');
};

/**
 * Clear all local auth state (tokens + user data).
 * Shared by useAuth().signOut() and the query-client 401 handler.
 * Does NOT call Auth0 logout or backend — callers handle those separately.
 */
export const clearAllAuthState = (): void => {
  clearAuthTokens();
  localStorage.removeItem('curaition_user');
};

// Initialize legacy token from localStorage on load
if (typeof window !== 'undefined') {
  authToken = localStorage.getItem('auth_token');
}
